import { useState, type FormEvent } from "react";
import { ApiError, apiRequest } from "../api";
import type { TokenResponse } from "../types";

interface LoginPageProps {
  onAuthenticated: (response: TokenResponse) => void;
}

export function LoginPage({ onAuthenticated }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const response = await apiRequest<TokenResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      onAuthenticated(response);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Unable to sign in.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-layout">
      <section className="brand-panel" aria-labelledby="brand-title">
        <div className="brand-mark" aria-hidden="true">FT</div>
        <p className="eyebrow">TRAIN WITH CONTEXT</p>
        <h1 id="brand-title">Your training life, connected.</h1>
        <p className="brand-copy">
          Plans, recovery, nutrition, and one live workout shared across iPhone,
          Apple Watch, and web.
        </p>
        <div className="signal-card">
          <span className="signal-dot" />
          <div><strong>Cross-device live sync</strong><small>Start on one screen. Continue on another.</small></div>
        </div>
      </section>

      <section className="auth-card" aria-labelledby="auth-title">
        <div>
          <p className="eyebrow">WELCOME BACK</p>
          <h2 id="auth-title">Sign in to FitTracker</h2>
          <p className="muted">Use the email and password from your mobile account.</p>
        </div>
        <form onSubmit={submit}>
          <label className="field">Email
            <input type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
          </label>
          <label className="field">Password
            <input type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} />
          </label>
          <p className="error" role="alert">{error}</p>
          <button className="button primary" type="submit" disabled={submitting}>
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </section>
    </main>
  );
}
